import React, { useState } from 'react';
import styled from 'styled-components';
import { SearchableSelect } from 'components/FormFields';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const stateOptions = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'New York', value: 'NY' },
  { label: 'Texas', value: 'TX' },
];

const trackOptions = [
  { label: 'Daytona International Speedway', value: 1 },
  { label: 'Indianapolis Motor Speedway', value: 2 },
  { label: 'Charlotte Motor Speedway', value: 3 },
  { label: 'Bristol Motor Speedway', value: 4 },
  { label: 'Talladega Superspeedway', value: 5 },
];

const SearchableSelectDocs = () => {
  const [basicValue, setBasicValue] = useState(null);
  const [trackValue, setTrackValue] = useState(null);
  const [creatableValue, setCreatableValue] = useState(null);

  const props = [
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label text displayed above the select',
    },
    {
      name: 'required',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows a red asterisk next to the label',
    },
    {
      name: 'value',
      type: 'string | number',
      required: false,
      description: 'The currently selected value',
    },
    {
      name: 'onChange',
      type: 'function',
      required: true,
      description:
        'Callback when selection changes. Receives the selected option object {label, value} or null when cleared.',
    },
    {
      name: 'placeholder',
      type: 'string',
      default: '"Start typing to search..."',
      required: false,
      description: 'Placeholder text for the search input',
    },
    {
      name: 'options',
      type: 'array',
      default: '[]',
      required: false,
      description: 'Array of {label, value} objects',
    },
    {
      name: 'loading',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows loading state in the dropdown',
    },
    {
      name: 'onSearch',
      type: 'function',
      required: false,
      description:
        'Callback when search text changes (for async/server-side search)',
    },
    {
      name: 'minSearchLength',
      type: 'number',
      default: '0',
      required: false,
      description: 'Minimum characters before filtering/searching begins',
    },
    {
      name: 'noResultsText',
      type: 'string',
      default: '"No results found"',
      required: false,
      description: 'Text shown when no options match the search',
    },
    {
      name: 'helperText',
      type: 'string',
      required: false,
      description: 'Helper or error text below the select',
    },
    {
      name: 'error',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows error state styling',
    },
    {
      name: 'width',
      type: 'string',
      default: '"100%"',
      required: false,
      description: 'Width of the select field',
    },
    {
      name: 'maxWidth',
      type: 'string',
      required: false,
      description: 'Maximum width of the select field',
    },
    {
      name: 'disabled',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Disables the select',
    },
    {
      name: 'creatable',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Enables creating new options inline',
    },
    {
      name: 'onCreateOption',
      type: 'function',
      required: false,
      description:
        'Async callback when creating a new option. Receives label string, should return Promise with {label, value}.',
    },
    {
      name: 'createLabel',
      type: 'string',
      default: '"Create"',
      required: false,
      description: 'Label for the create option row',
    },
  ];

  return (
    <>
      <PageTitle>SearchableSelect</PageTitle>
      <PageDescription>
        A searchable dropdown component with type-ahead filtering, keyboard
        navigation, clear button, and optional create-new-option functionality.
        Ideal for selecting from large lists of options.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Searchable Select</SectionTitle>
        <ComponentDemo label="Type to filter options" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <SearchableSelect
              label="State"
              placeholder="Search for a state..."
              options={stateOptions}
              value={basicValue}
              onChange={(option) => setBasicValue(option?.value || null)}
            />
            {basicValue && (
              <SelectedValue>
                Selected: <code>{basicValue}</code>
              </SelectedValue>
            )}
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { SearchableSelect } from 'components/FormFields';

const [value, setValue] = useState(null);

<SearchableSelect
  label="State"
  placeholder="Search for a state..."
  options={stateOptions}
  value={value}
  onChange={(option) => setValue(option?.value || null)}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Required and Helper Text</SectionTitle>
        <ComponentDemo label="Required with helper text" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <SearchableSelect
              label="Track"
              required
              placeholder="Search tracks..."
              options={trackOptions}
              value={trackValue}
              onChange={(option) => setTrackValue(option?.value || null)}
              helperText="Select the track for this event"
            />
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>With Error State</SectionTitle>
        <ComponentDemo label="Error validation" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <SearchableSelect
              label="Venue"
              required
              placeholder="Search venues..."
              options={[]}
              value={null}
              onChange={() => undefined}
              error
              helperText="Please select a venue"
            />
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>With Creatable Option</SectionTitle>
        <ComponentDemo
          label="Type a name that doesn't exist to see 'Create' option"
          align="stretch"
        >
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <SearchableSelect
              label="Company"
              placeholder="Search or create a company..."
              options={[
                { label: 'Acme Corp', value: 1 },
                { label: 'Globex Inc', value: 2 },
                { label: 'Initech', value: 3 },
              ]}
              value={creatableValue}
              onChange={(option) => setCreatableValue(option?.value || null)}
              creatable
              onCreateOption={async (name) => {
                // Simulate API call
                return { label: name, value: Date.now() };
              }}
              createLabel="Create company"
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`// With creatable option
<SearchableSelect
  label="Company"
  placeholder="Search or create..."
  options={companies}
  value={companyId}
  onChange={(option) => setCompanyId(option?.value || null)}
  creatable
  onCreateOption={async (name) => {
    const result = await createCompany({ name });
    return { label: result.name, value: result.id };
  }}
  createLabel="Create company"
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Disabled State</SectionTitle>
        <ComponentDemo label="Disabled searchable select" align="stretch">
          <div style={{ width: '400px', maxWidth: '100%' }}>
            <SearchableSelect
              label="Region"
              placeholder="Search regions..."
              options={stateOptions}
              value="CA"
              onChange={() => undefined}
              disabled
            />
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock>
          {`import { SearchableSelect } from 'components/FormFields';
// or
import SearchableSelect from 'components/FormFields/SearchableSelect';`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>Type-ahead search with local filtering</li>
          <li>Optional async search via onSearch callback</li>
          <li>Keyboard navigation (Arrow keys, Enter, Escape)</li>
          <li>Clear button to reset selection</li>
          <li>Optional create-new-option inline</li>
          <li>Theme-aware (light/dark mode)</li>
          <li>Error state and helper text support</li>
        </ul>
      </Section>

      <Section>
        <SectionTitle>When to Use</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>
            <strong>SearchableSelect</strong> - Large option lists where users
            need to search/filter (states, tracks, users)
          </li>
          <li>
            <strong>Select</strong> - Small, predefined lists (status, priority)
            where search isn&apos;t needed
          </li>
          <li>
            <strong>CreatableSelect</strong> - When you only need create
            functionality without search
          </li>
          <li>
            <strong>MultiSelect</strong> - When users need to select multiple
            items
          </li>
        </ul>
      </Section>
    </>
  );
};

export default SearchableSelectDocs;
